var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, Vue } from 'vue-property-decorator';
import { DateTime } from 'luxon';
import ErrorHandler from '@/support/ErrorHandler';
import { RpcClient } from '@/http';
import { Group } from '@/models/Group';
import ALoader from '@/components/ALoader/ALoader.vue';
import SubjectOverviewNew from '@/components/subject-overview-new/SubjectOverviewNew.vue';
import { Label } from '@/models/Label';
import { Subject } from '@/models/Subject';
import { isEmpty, omitBy } from 'lodash';
import client from '@/support/Client';
import { UserSubjectStatusEnum } from '@/models/UserSubject';
let TimeslotGoalOverview = class TimeslotGoalOverview extends Vue {
    constructor() {
        // #region @VModel
        // #endregion
        super(...arguments);
        // #region @PropSyncs
        // #endregion
        // #region @Props
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.isLoading = true;
        this.group = null;
        this.timeslot = null;
        this.groupTemplates = null;
        this.hasError = false;
        this.labels = {};
        this.isDownloading = false;
        this.componentType = 'status';
        // #endregion
        // #region @Watchers
        // #endregion
    }
    // #endregion
    // #region Lifecycle Hooks
    mounted() {
        this.initialize();
    }
    async initialize() {
        this.isLoading = true;
        await this.fetchGroup();
        if (this.group) {
            await this.fetchGroupTemplates();
            await this.fetchLabels();
        }
        else {
            this.hasError = true;
        }
        this.isLoading = false;
    }
    // #endregion
    // #region Event handlers
    onToggleComponentState() {
        this.componentType = this.componentType === 'status' ? 'notes' : 'status';
    }
    onAddNoteForAllStudents() {
        if (!this.subjectOverviewNewComponent) {
            return;
        }
        this.subjectOverviewNewComponent.onAddNoteForAllStudents();
    }
    // #endregion
    // #region Class methods
    // #endregion
    // #region Async methods (api calls only)
    async fetchGroup() {
        try {
            this.group = await new Group().find(this.$route.params.groupId);
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async fetchGroupTemplates() {
        if (!this.group) {
            return;
        }
        const { date, activeStart } = this.$route.query;
        if (!date || !activeStart) {
            this.hasError = true;
            return;
        }
        try {
            this.groupTemplates = await new RpcClient().execute('get-template-for-group', {
                group_id: this.group.id,
                starts_at: activeStart,
                type: 'week',
            });
            if (!this.groupTemplates) {
                this.hasError = true;
                return;
            }
            const timeslots = this.groupTemplates[date];
            if (!timeslots) {
                this.hasError = true;
                return;
            }
            const timeslot = timeslots.find((slot) => slot.id === this.$route.params.id);
            if (!timeslot) {
                this.hasError = true;
                return;
            }
            this.timeslot = timeslot;
        }
        catch (error) {
            this.hasError = true;
            ErrorHandler.network(error);
        }
    }
    async fetchLabels() {
        try {
            const labels = await new Label().limit(999).all();
            this.labels = labels.reduce((accumulator, currentLabel) => {
                const { key, value } = currentLabel;
                if (key) {
                    accumulator[key] = value || '';
                }
                return accumulator;
            }, {});
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async onClickDownloadOverview() {
        this.isDownloading = true;
        const filters = {
            groups: this.group ? [this.group.id] : [],
            subjects: this.timeslotSubjects.map((subject) => subject.id || ''),
        };
        try {
            const parsed = omitBy(filters, isEmpty);
            const download = await client('get', 'subjects/export-summary', {
                filters: parsed,
                ...{ with: ['parent'] },
            });
            window.open(download.data.pdf, '_blank');
        }
        catch (error) {
            ErrorHandler.network(error);
        }
        finally {
            this.isDownloading = false;
        }
    }
    // #endregion
    // #region Getters & Setters
    get subjectOverviewFilters() {
        const users = this.group && this.group.users ? this.group.users : [];
        return {
            groups: this.group ? [this.group.id] : [],
            // users: users.length > 0 ? (users.map((user: User) => user.id) as string[]) : ([] as string[]),
            statuses: [
                UserSubjectStatusEnum.HIGHLIGHTED,
                UserSubjectStatusEnum.LESSON_DONE,
                UserSubjectStatusEnum.BUSY,
                UserSubjectStatusEnum.OLD,
            ],
            trashed: false,
        };
    }
    get formattedDateTime() {
        if (!this.timeslot) {
            return '';
        }
        const dateParam = this.$route.query.date;
        const formattedDate = DateTime.fromISO(dateParam).toFormat('cccc, dd LLLL yyyy');
        return (formattedDate +
            ' - ' +
            this.timeslot.starts_at.substring(0, 5) +
            '-' +
            this.timeslot.ends_at.substring(0, 5));
    }
    get breadcrumbs() {
        return [
            {
                name: this.group && this.group.name ? this.group.name : this.$tc('Group'),
                path: `/groups/${this.$route.params.groupId}`,
            },
            {
                name: this.$tc('Period planner'),
                path: `/groups/${this.$route.params.groupId}/goal-planner`,
            },
        ];
    }
    get timeslotSubjects() {
        if (!this.timeslot || !this.timeslot.subjects) {
            return [];
        }
        return this.timeslot.subjects.map((subject) => new Subject(subject));
    }
    get subjectOverviewNewComponent() {
        return (this.$refs && this.$refs.subjectOverviewNewComponent) || null;
    }
};
TimeslotGoalOverview = __decorate([
    Component({
        components: {
            ALoader,
            SubjectOverviewNew,
        },
    })
], TimeslotGoalOverview);
export default TimeslotGoalOverview;
// #region Enums
// #endregion
// #region Interfaces
// #endregion
// #region Constants
// #endregion
